import { Link } from 'react-router-dom';
import { ClipboardCheck, FileText, Sparkles, Coins, Plus, ArrowRight, Receipt, Gift, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { REFERRAL_REWARD_CREDITS } from '../services/referralsService';

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;

  // Any user without a verified phone is in a degraded referral state — they
  // can't share a link and (if referred) haven't claimed their bonus. A
  // single banner covers both cases, with copy that branches on whether
  // they were referred.
  const needsPhoneVerify = !user.phoneVerified;
  const hasUnclaimedReferralBonus = Boolean(user.referredBy) && !user.referralBonusAwarded;

  return (
    <div className="container-wide py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold">Hi, {user.name.split(' ')[0]} 👋</h1>
          <p className="text-ink-600 mt-1">Pick an action to get started.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="chip bg-brand-50 text-brand-700 border border-brand-100 text-sm px-3 py-1.5">
            <Coins className="w-4 h-4 mr-1.5" /> {user.credits} credits left
          </span>
          <Link to="/pricing" className="btn-secondary btn-sm">
            <Plus className="w-4 h-4" /> Top up
          </Link>
          <Link to="/billing" className="btn-secondary btn-sm" title="Payment history">
            <Receipt className="w-4 h-4" /> Billing
          </Link>
          <Link to="/referrals" className="btn-secondary btn-sm" title="Invite friends">
            <Gift className="w-4 h-4" /> Invite
          </Link>
        </div>
      </div>

      {needsPhoneVerify && hasUnclaimedReferralBonus && (
        <div className="mt-6 card p-4 bg-emerald-50 border-emerald-100 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-emerald-800">Your referral bonus is waiting</div>
            <div className="text-emerald-700">
              Verify your phone number to claim {REFERRAL_REWARD_CREDITS} free credits — your friend gets {REFERRAL_REWARD_CREDITS} too.
            </div>
          </div>
          <Link to="/referrals" className="btn-primary btn-sm">Verify phone</Link>
        </div>
      )}

      {needsPhoneVerify && !hasUnclaimedReferralBonus && (
        <div className="mt-6 card p-4 bg-brand-50 border-brand-100 flex items-center gap-3">
          <Gift className="w-5 h-5 text-brand-700 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-brand-800">Unlock your referral link</div>
            <div className="text-brand-700">
              Verify your phone once to activate a shareable link — earn {REFERRAL_REWARD_CREDITS} credits each time a friend joins.
            </div>
          </div>
          <Link to="/referrals" className="btn-primary btn-sm">Verify phone</Link>
        </div>
      )}

      <div className="mt-8 grid gap-6 md:grid-cols-3">
        <ActionCard
          to="/evaluate"
          icon={<FileText className="w-5 h-5" />}
          title="Evaluate a report"
          body="Upload a .docx or .pdf. Get section-by-section scores and improvement tips."
          badge="1 credit"
        />
        <ActionCard
          to="/generate"
          icon={<Sparkles className="w-5 h-5" />}
          title="Generate a report"
          body="Give us a topic. We draft a structured report in your preferred tone."
          badge="1 credit"
        />
        <ActionCard
          to="/rubric"
          icon={<ClipboardCheck className="w-5 h-5" />}
          title="Grade by rubric"
          body="Upload or paste a rubric. We score per criterion with weighted totals."
          badge="1 credit"
          highlight
        />
      </div>

      <section className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-semibold">Recent activity</h2>
          <Link to="/history" className="text-sm text-brand-700 hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-4 card divide-y divide-ink-100">
          <EmptyRow />
        </div>
      </section>

      <section className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="card p-6 bg-gradient-to-br from-brand-600 to-fuchsia-600 text-white border-0">
          <h3 className="text-lg font-semibold">Upgrade to Pro</h3>
          <p className="text-brand-100 text-sm mt-1 max-w-lg">
            200 operations per month, the "Improve to A-grade" rewriter, a custom rubric library, and priority processing — for $19/month.
          </p>
          <Link to="/pricing" className="btn-lg bg-white text-brand-700 mt-4 inline-flex hover:bg-brand-50">See plans</Link>
        </div>

        <div className="card p-6 bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-0">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Gift className="w-5 h-5" /> Invite friends, get credits
          </h3>
          <p className="text-emerald-50 text-sm mt-1 max-w-lg">
            {needsPhoneVerify
              ? <>Verify your phone to activate a shareable link — you'll get {REFERRAL_REWARD_CREDITS} credits for every friend who joins.</>
              : <>Share your link. Anyone who joins and verifies their phone gets {REFERRAL_REWARD_CREDITS} free credits — and so do you.</>}
          </p>
          <Link to="/referrals" className="btn-lg bg-white text-emerald-700 mt-4 inline-flex hover:bg-emerald-50">
            {needsPhoneVerify ? 'Verify phone' : 'Get your link'}
          </Link>
        </div>
      </section>
    </div>
  );
}

function ActionCard({ to, icon, title, body, badge, highlight }:
  { to: string; icon: React.ReactNode; title: string; body: string; badge?: string; highlight?: boolean }) {
  return (
    <Link to={to} className={`card-hover p-6 block ${highlight ? 'ring-2 ring-brand-500' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 grid place-items-center">{icon}</div>
        {badge && <span className="chip bg-ink-100 text-ink-600">{badge}</span>}
      </div>
      <h3 className="mt-4 font-semibold text-ink-900">{title}</h3>
      <p className="mt-2 text-sm text-ink-600">{body}</p>
      <span className="mt-4 inline-flex items-center text-sm text-brand-700 font-medium">
        Start <ArrowRight className="w-4 h-4 ml-1" />
      </span>
    </Link>
  );
}

function EmptyRow() {
  return (
    <div className="p-10 text-center text-ink-500 text-sm">
      No recent activity yet. Your grading and generation runs will show up here.
    </div>
  );
}
